import React, { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import useApi from "../../hooks/useApi";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, PieChart, Pie, Cell
} from "recharts";

const COLORS = ["#22c55e", "#ef4444"];

const BusinessAdminDashboardPage = () => {
  const api = useApi();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalTransactions: 0,
    totalRevenue: 0,
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    organizations: 0,
  });
  const [loading, setLoading] = useState(true);

  // Analytics for charts
  const [monthlyRevenue, setMonthlyRevenue] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState({ active: 0, expired: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const {data:res} = await api.get("business-admin/analytics");
        setStats({
          totalUsers: res.totalUsers || 0,
          totalTransactions: res.totalTransactions || 0,
          totalRevenue: res.totalRevenue || 0,
          activeSubscriptions: res.activeSubscriptions || 0,
          expiredSubscriptions: res.expiredSubscriptions || 0,
          organizations: res.organizations || 0,
        });
        setMonthlyRevenue(res.monthlyRevenue || []);
        setUserGrowth(res.userGrowth || []);
        setSubscriptionStatus({
          active: res.activeSubscriptions || 0,
          expired: res.expiredSubscriptions || 0,
        });
      } catch (err) {
        // Optionally handle error
      }
      setLoading(false);
    };
    fetchStats();
  }, [api]);

  // Prepare pie data for subscriptions
  const subscriptionPieData = [
    { name: "Active", value: subscriptionStatus.active },
    { name: "Expired", value: subscriptionStatus.expired },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-10 px-4">
        <h1 className="text-2xl font-bold mb-8">Business Admin Dashboard</h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Total Users</div>
            <div className="text-3xl font-bold text-indigo-600">
              {loading ? "--" : stats.totalUsers}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Total Organizations</div>
            <div className="text-3xl font-bold text-indigo-600">
              {loading ? "--" : stats.organizations}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Active Subscriptions</div>
            <div className="text-3xl font-bold text-green-600">
              {loading ? "--" : stats.activeSubscriptions}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Expired Subscriptions</div>
            <div className="text-3xl font-bold text-red-500">
              {loading ? "--" : stats.expiredSubscriptions}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Total Transactions</div>
            <div className="text-3xl font-bold text-indigo-600">
              {loading ? "--" : stats.totalTransactions}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-gray-500 text-sm mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-yellow-600">
              {loading ? "--" : `$${stats.totalRevenue.toLocaleString()}`}
            </div>
          </div>
        </div>

        {/* Analytics Graphs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Monthly Revenue</h2>
            {loading ? (
              <div className="text-center text-gray-400">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyRevenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="amount" fill="#6366f1" name="Revenue ($)" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">User Growth</h2>
            {loading ? (
              <div className="text-center text-gray-400">Loading...</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={userGrowth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="count" stroke="#10b981" name="New Users" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="max-w-md mx-auto bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-center">Subscription Status</h2>
          {loading ? (
            <div className="text-center text-gray-400">Loading...</div>
          ) : (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={subscriptionPieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {subscriptionPieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BusinessAdminDashboardPage;